import { createResolverRegistry } from '../resolvers/resolverRegistry';
import { createEntityLifecycleManager } from '../lifecycleManager/entityLifecycleManager';
import { create, isRecursionError } from 'event-emitter';
import { lifecycleRules } from '../resolvers/decorators/lifecycleRules';
import { createLogger } from '../devtools/logger';
export const createController = ({
  httpClient,
  globalDecorators = [],
  createDevtools
}) => {
  const devtoolsCallbacks = {};
  const logger = createLogger({
    onLog: log => {
      var _devtoolsCallbacks$on;
      return (_devtoolsCallbacks$on = devtoolsCallbacks.onLog) === null || _devtoolsCallbacks$on === void 0 ? void 0 : _devtoolsCallbacks$on.call(devtoolsCallbacks, log);
    },
    defaultTags: {
      component: 'controller'
    }
  });
  logger.debug('startup');
  const stateEmitter = create({
    onEmitError: ({
      error
    }) => {
      logger.error('error emitting state', {
        error: error.message
      });
      return !!isRecursionError(error);
    }
  });
  const entityDefinitions = new Map();
  const registerEntityDef = entity => {
    const current = entityDefinitions.get(entity.typeName);
    if (current !== entity) {
      var _devtoolsCallbacks$on2;
      entityDefinitions.set(entity.typeName, entity);
      logger.debug('registering entity', {
        typeName: entity.typeName
      });
      (_devtoolsCallbacks$on2 = devtoolsCallbacks.onRegisterEntityDefinition) === null || _devtoolsCallbacks$on2 === void 0 || _devtoolsCallbacks$on2.call(devtoolsCallbacks, entity);
    }
  };
  const resolverRegistry = createResolverRegistry({
    logger: logger.withTags({
      component: 'resolverRegistry'
    }),
    httpClient,
    globalDecorators: [lifecycleRules, ...globalDecorators]
  });
  const lifecycleManager = createEntityLifecycleManager({
    logger: logger.withTags({
      component: 'lifecycleManager'
    }),
    onStateChange: (entity, instanceKey, state) => {
      var _devtoolsCallbacks$on3;
      stateEmitter.emit(instanceKey, state);
      logger.debug('state change', {
        typeName: entity.typeName,
        instanceKey
      });
      (_devtoolsCallbacks$on3 = devtoolsCallbacks.onStateChange) === null || _devtoolsCallbacks$on3 === void 0 || _devtoolsCallbacks$on3.call(devtoolsCallbacks, entity.typeName, instanceKey);
    },
    onInvalidate: (entity, instanceKey) => {
      var _devtoolsCallbacks$on4;
      const invalidateLogger = logger.withTags({
        typeName: entity.typeName,
        instanceKey
      });
      invalidateLogger.debug('invalidating');
      if (resolverRegistry.isAttached({
        instanceKey,
        entity
      })) {
        invalidateLogger.info('refreshing resolver');
        resolverRegistry.refresh({
          instanceKey,
          entity
        });
      } else {
        invalidateLogger.info('marking stale');

        // No resolver attached, manually mark this entity as stale for next attachment
        lifecycleManager.markStale(entity, instanceKey);
      }
      (_devtoolsCallbacks$on4 = devtoolsCallbacks.onInvalidate) === null || _devtoolsCallbacks$on4 === void 0 || _devtoolsCallbacks$on4.call(devtoolsCallbacks, entity.typeName, instanceKey);
    }
  });
  const watch = ({
    entity,
    params = {},
    instanceKey = entity.identifyInstance(params)
  }, callback) => {
    registerEntityDef(entity);
    const watchLogger = logger.withTags({
      typeName: entity.typeName,
      instanceKey
    });
    watchLogger.info('watching entity');
    const getState = () => lifecycleManager.getState(entity, instanceKey);
    const initialCount = stateEmitter.count(instanceKey);
    const initialState = getState();
    const off = stateEmitter.on(instanceKey, callback !== null && callback !== void 0 ? callback : () => {});

    // Provide initial state if present. Only passed to this callback.
    if (initialState) {
      watchLogger.debug('providing initial state');
      try {
        callback === null || callback === void 0 || callback(initialState, off);
      } catch (error) {
        watchLogger.error('error providing initial state', {
          error: error.message
        });
      }
    }

    // Attach if first subscriber
    if (initialCount === 0) {
      var _devtoolsCallbacks$on5;
      watchLogger.info('attaching resolver');
      resolverRegistry.attach({
        entity,
        instanceKey,
        params,
        getState,
        onResolverUpdate: resolverState => lifecycleManager.handleStateChange(entity, instanceKey, resolverState)
      });

      // Attachment may have initialized state, so let's get a fresh copy
      const state = getState();
      if (!state || state.stale || state.loading) {
        watchLogger.info('requesting initial refresh');
        resolverRegistry.refresh({
          instanceKey,
          entity
        });
      }
      (_devtoolsCallbacks$on5 = devtoolsCallbacks.onAttachmentChange) === null || _devtoolsCallbacks$on5 === void 0 || _devtoolsCallbacks$on5.call(devtoolsCallbacks, instanceKey, true);
    }
    return () => {
      watchLogger.info('unwatching entity');

      // Detach if last subscriber
      if (off() === 0) {
        var _devtoolsCallbacks$on6;
        watchLogger.info('detaching resolver');
        resolverRegistry.detach({
          instanceKey,
          entity
        });
        (_devtoolsCallbacks$on6 = devtoolsCallbacks.onAttachmentChange) === null || _devtoolsCallbacks$on6 === void 0 || _devtoolsCallbacks$on6.call(devtoolsCallbacks, instanceKey, false);
        // TODO: Queue for cache GC
      }
    };
  };
  createDevtools === null || createDevtools === void 0 || createDevtools({
    logger: logger.withTags({
      component: 'devtools'
    }),
    entityDefinitions,
    lifecycleManager,
    resolverRegistry,
    devtoolsCallbacks
  });
  return {
    watch,
    invalidate: ({
      entity,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      const invalidateLogger = logger.withTags({
        typeName: entity.typeName,
        instanceKey
      });
      invalidateLogger.info('invalidating entity');
      return lifecycleManager.invalidate(entity, instanceKey);
    },
    getState: ({
      entity,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      return lifecycleManager.getState(entity, instanceKey);
    },
    setState: ({
      entity,
      state,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      const setStateLogger = logger.withTags({
        typeName: entity.typeName,
        instanceKey
      });
      const nextState = typeof state === 'function' ? state(lifecycleManager.getState(entity, instanceKey)) : state;
      if (nextState) {
        setStateLogger.info('setting state');
        lifecycleManager.handleStateChange(entity, instanceKey, nextState);
      } else {
        setStateLogger.debug('functional state update returned void');
      }
    }
  };
};